﻿import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Text, Heading, Input, Select, Button, SortableTableHeader, LoadingSpinner, Tooltip } from '../components/ui'
import { PermissionGuard } from '../components/PermissionGuard'
import { usePermission } from '../hooks/usePermissions'
import { modulesService } from '../services/modules'
import { cctBuscasService } from '../services/cctBuscas'
import type { CctBuscaAditivo, CctBuscaLista } from '../types/cctBuscas'
import { 
  ExclamationTriangleIcon, 
  XMarkIcon,
  QuestionMarkCircleIcon,
  PaperAirplaneIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline'

const WEBHOOK_INSERIR_CCT_SCGP = 'https://webhook.pasiai.com/webhook/InserirCCTSCGP'

type ActiveTab = 'ccts' | 'aditivos'
type TipoFiltroData = 'data' | 'mes' | 'ano' | 'hoje'
type OperadorFiltroData = 'igual' | 'a_partir_de' | 'ate'
type SortKeyCct = 'id' | 'cct_convencao_id' | 'created_at' | 'status' | 'id_scgp' | 'vigencia_inicio' | 'vigencia_fim' | 'codigo' | 'data_importacao' | 'cnpj_entidade_laboral' | 'cnpj_entidade_patronal' | 'resposta'
type SortKeyAditivo = 'created_at' | 'status' | 'cnpj' | 'indice' | 'registro' | 'vigencia_inicio' | 'vigencia_fim'

const tabClass = (active: boolean) =>
  `py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
    active
      ? 'border-primary-600 text-primary-600 dark:text-primary-400'
      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
  }`

const statusNormalizado = (s: string | null) => {
  const u = (s || '').trim().toUpperCase()
  if (u === 'NÃO LOCALIZADA MTE' || u === 'NAO LOCALIZADA MTE' || u === 'NÃO LOCALIZADA' || u === 'NAO LOCALIZADA') return 'NÃO LOCALIZADA'
  if (u === 'IMPORTADO SCGP' || u === 'IMPORTADO') return 'IMPORTADO SCGP'
  if (u === 'DISPONÍVEL PARA IMPORTAR') return 'DISPONÍVEL PARA IMPORTAR'
  return u || ''
}

const statusNormalizadoAditivo = (s: string | null) => {
  const u = (s || '').replace(/\u00a0/g, ' ').trim().toUpperCase().replace(/\s+/g, ' ')
  const semAcento = u.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  if (
    semAcento === 'LOCALIZADA' ||
    semAcento === 'LOCALIZADO' ||
    semAcento === 'LOCALIZADO MTE' ||
    semAcento === 'LOCALIZADA MTE'
  ) {
    return 'LOCALIZADA'
  }
  if (
    semAcento === 'NAO LOCALIZADA' ||
    semAcento === 'NAO LOCALIZADO' ||
    semAcento === 'NAO LOCALIZADO MTE' ||
    semAcento === 'NAO LOCALIZADA MTE'
  ) {
    return 'NÃO LOCALIZADA'
  }
  if (semAcento === 'SEM ALTERACAO') return 'SEM ALTERAÇÃO'
  if (semAcento === 'DISPONIVEL PARA IMPORTAR') return 'DISPONÍVEL PARA IMPORTAR'
  if (semAcento === 'IMPORTADO SCGP' || semAcento === 'IMPORTADO') return 'IMPORTADO SCGP'
  return u || ''
}

/** Mesmo padrão da aba CCTs: amarelo = não localizada, azul = localizada/disponível, verde = importado. */
const statusColorClass = (norm: string) => {
  if (norm === 'NÃO LOCALIZADA') return 'text-yellow-600 dark:text-yellow-400'
  if (norm === 'DISPONÍVEL PARA IMPORTAR' || norm === 'LOCALIZADA') return 'text-blue-600 dark:text-blue-400'
  if (norm === 'IMPORTADO SCGP') return 'text-green-600 dark:text-green-400'
  if (norm === 'SEM ALTERAÇÃO') return 'text-gray-500 dark:text-gray-400'
  return 'text-gray-700 dark:text-gray-300'
}

const matchFiltroData = (
  dataBusca: string | null | undefined,
  filtroDataUltimaBusca: string,
  operadorFiltroDataUltimaBusca: OperadorFiltroData,
): boolean => {
  if (!filtroDataUltimaBusca) return true
  if (!dataBusca) return false

  const raw = String(dataBusca).trim()
  const dataStr = raw.match(/^\d{4}-\d{2}-\d{2}(T|$)/)
    ? raw.slice(0, 10)
    : (() => {
        const d = new Date(dataBusca)
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      })()
  const filtroStr = filtroDataUltimaBusca.trim()
  const isIgual = operadorFiltroDataUltimaBusca === 'igual'
  const isAte = operadorFiltroDataUltimaBusca === 'ate'

  if (filtroStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return isIgual ? dataStr === filtroStr : isAte ? dataStr <= filtroStr : dataStr >= filtroStr
  }
  if (filtroStr.match(/^\d{4}-\d{2}$/)) {
    const [ano, mes] = filtroStr.split('-')
    const anoItem = parseInt(dataStr.slice(0, 4))
    const mesItem = parseInt(dataStr.slice(5, 7))
    return isIgual
      ? anoItem === parseInt(ano) && mesItem === parseInt(mes)
      : isAte
        ? anoItem < parseInt(ano) || (anoItem === parseInt(ano) && mesItem <= parseInt(mes))
        : anoItem > parseInt(ano) || (anoItem === parseInt(ano) && mesItem >= parseInt(mes))
  }
  if (filtroStr.match(/^\d{4}$/)) {
    const anoItem = parseInt(dataStr.slice(0, 4))
    return isIgual ? anoItem === parseInt(filtroStr) : isAte ? anoItem <= parseInt(filtroStr) : anoItem >= parseInt(filtroStr)
  }
  return true
}

const formatDate = (dateString: string | null) => {
  if (!dateString) return '—'
  try {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return dateString
  }
}

const formatDateShort = (dateString: string | null) => {
  if (!dateString) return '—'
  try {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  } catch {
    return dateString
  }
}

const isEmptyText = (v: unknown) => {
  const str = v == null ? '' : String(v).trim()
  return str === '' || str.toLowerCase() === 'null' || str.toLowerCase() === 'undefined'
}

const DateFilterControls: React.FC<{
  operador: OperadorFiltroData
  setOperador: (v: OperadorFiltroData) => void
  tipo: TipoFiltroData
  setTipo: (v: TipoFiltroData) => void
  valor: string
  setValor: (v: string) => void
}> = ({ operador, setOperador, tipo, setTipo, valor, setValor }) => (
  <div className="mt-4">
    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
      Data última busca MTE
    </Text>
    <div className="flex flex-nowrap gap-2 items-end flex-wrap">
      <Select
        value={operador}
        onChange={(e) => setOperador(e.target.value as OperadorFiltroData)}
        className="w-32 shrink-0"
      >
        <option value="igual">Igual</option>
        <option value="a_partir_de">A partir de</option>
        <option value="ate">Até</option>
      </Select>
      <Select
        value={tipo}
        onChange={(e) => {
          const novo = e.target.value as TipoFiltroData
          setTipo(novo)
          if (novo === 'hoje') {
            setValor(new Date().toISOString().slice(0, 10))
          } else {
            setValor('')
          }
        }}
        className="w-32 shrink-0"
      >
        <option value="data">Data</option>
        <option value="hoje">Hoje</option>
        <option value="mes">Mês</option>
        <option value="ano">Ano</option>
      </Select>
      {tipo === 'data' && (
        <Input type="date" value={valor} onChange={(e) => setValor(e.target.value)} className="flex-1 min-w-0 max-w-xs" />
      )}
      {tipo === 'hoje' && (
        <Input
          type="date"
          value={valor || new Date().toISOString().slice(0, 10)}
          onChange={(e) => setValor(e.target.value)}
          className="flex-1 min-w-0 max-w-xs"
        />
      )}
      {tipo === 'mes' && (
        <Input type="month" value={valor} onChange={(e) => setValor(e.target.value)} className="flex-1 min-w-0 max-w-xs" />
      )}
      {tipo === 'ano' && (
        <Input
          type="number"
          min={1900}
          max={2100}
          placeholder="Ano (ex: 2024)"
          value={valor || ''}
          onChange={(e) => setValor(e.target.value.replace(/\D/g, '').slice(0, 4))}
          className="w-24 shrink-0"
        />
      )}
    </div>
  </div>
)

const PaginationControls: React.FC<{
  currentPage: number
  totalPages: number
  startIndex: number
  endIndex: number
  total: number
  onPageChange: (page: number) => void
  onPrevious: () => void
  onNext: () => void
}> = ({ currentPage, totalPages, startIndex, endIndex, total, onPageChange, onPrevious, onNext }) => {
  if (totalPages <= 1) return null
  return (
    <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between">
        <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
          <Text size="sm">
            Mostrando {startIndex + 1} a {Math.min(endIndex, total)} de {total} registros
          </Text>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={onPrevious}
            disabled={currentPage === 1}
            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Anterior
          </button>
          <div className="flex space-x-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNumber: number
              if (totalPages <= 5) {
                pageNumber = i + 1
              } else if (currentPage <= 3) {
                pageNumber = i + 1
              } else if (currentPage >= totalPages - 2) {
                pageNumber = totalPages - 4 + i
              } else {
                pageNumber = currentPage - 2 + i
              }
              return (
                <button
                  key={pageNumber}
                  onClick={() => onPageChange(pageNumber)}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                    currentPage === pageNumber
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  {pageNumber}
                </button>
              )
            })}
          </div>
          <button
            onClick={onNext}
            disabled={currentPage === totalPages}
            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Próximo
          </button>
        </div>
      </div>
    </div>
  )
}

const CCTLista = () => {
  const { hasPermission: canInsert } = usePermission('ccts_lista', 'insert')
  const [activeTab, setActiveTab] = useState<ActiveTab>('ccts')
  const [cctsData, setCctsData] = useState<CctBuscaLista[]>([])
  const [aditivosData, setAditivosData] = useState<CctBuscaAditivo[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [refreshError, setRefreshError] = useState<string | null>(null)

  const [filtroCNPJLaboral, setFiltroCNPJLaboral] = useState('')
  const [filtroCNPJPatronal, setFiltroCNPJPatronal] = useState('')
  const [filtroStatus, setFiltroStatus] = useState('')
  const [filtroDataUltimaBusca, setFiltroDataUltimaBusca] = useState('')
  const [tipoFiltroDataUltimaBusca, setTipoFiltroDataUltimaBusca] = useState<TipoFiltroData>('data')
  const [operadorFiltroDataUltimaBusca, setOperadorFiltroDataUltimaBusca] = useState<OperadorFiltroData>('igual')

  const [filtroCnpjAditivo, setFiltroCnpjAditivo] = useState('')
  const [filtroStatusAditivo, setFiltroStatusAditivo] = useState('')
  const [filtroDataAditivo, setFiltroDataAditivo] = useState('')
  const [tipoFiltroDataAditivo, setTipoFiltroDataAditivo] = useState<TipoFiltroData>('data')
  const [operadorFiltroDataAditivo, setOperadorFiltroDataAditivo] = useState<OperadorFiltroData>('igual')

  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [selectedCCT, setSelectedCCT] = useState<CctBuscaLista | null>(null)
  const [detailsModalTab, setDetailsModalTab] = useState<'dados' | 'resposta' | 'clausulas'>('dados')
  const [isAditivoDetailsOpen, setIsAditivoDetailsOpen] = useState(false)
  const [selectedAditivo, setSelectedAditivo] = useState<CctBuscaAditivo | null>(null)
  const [aditivoDetailsTab, setAditivoDetailsTab] = useState<'dados' | 'resposta'>('dados')

  const [isEnviarScgpModalOpen, setIsEnviarScgpModalOpen] = useState(false)
  const [enviarScgpModalStatus, setEnviarScgpModalStatus] = useState<'idle' | 'confirm' | 'loading' | 'success' | 'error'>('idle')
  const [enviarScgpModalError, setEnviarScgpModalError] = useState<string | null>(null)
  const [enviarScgpModalConvencaoId, setEnviarScgpModalConvencaoId] = useState<number | null>(null)
  const [enviarScgpModalIdBusca, setEnviarScgpModalIdBusca] = useState<number | null>(null)
  
  const [sortBy, setSortBy] = useState<SortKeyCct>('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [sortByAditivo, setSortByAditivo] = useState<SortKeyAditivo>('created_at')
  const [sortOrderAditivo, setSortOrderAditivo] = useState<'asc' | 'desc'>('desc')
  
  const [currentPage, setCurrentPage] = useState(1)
  const [currentPageAditivo, setCurrentPageAditivo] = useState(1)
  const [itemsPerPage] = useState(20)

  const [moduleTitle, setModuleTitle] = useState<string>('CCTs • Resultado da busca')
  const [moduleDescription, setModuleDescription] = useState<string>('Resultado de buscas de CCTs')

  useEffect(() => {
    const fetchModuleInfo = async () => {
      try {
        const result = await modulesService.getByName('ccts_lista')
        if (result.success && result.data) {
          if (result.data.display_name) setModuleTitle(result.data.display_name)
          if (result.data.description) setModuleDescription(result.data.description)
        }
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : undefined
        console.error('Erro ao buscar informações do módulo:', msg)
      }
    }
    fetchModuleInfo()
  }, [])

  const fetchLista = useCallback(async (options?: { silent?: boolean }) => {
    const silent = Boolean(options?.silent)
    try {
      if (silent) {
        setRefreshError(null)
        setIsRefreshing(true)
      } else {
        setLoading(true)
        setError(null)
      }
      const [ccts, aditivos] = await Promise.all([
        cctBuscasService.getBuscasCcts(),
        cctBuscasService.getBuscasAditivos(),
      ])
      setCctsData(ccts)
      setAditivosData(aditivos)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao carregar'
      if (silent) {
        setRefreshError(msg)
      } else {
        setError(msg)
      }
    } finally {
      if (silent) {
        setIsRefreshing(false)
      } else {
        setLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    fetchLista()
  }, [fetchLista])

  const cctsFiltrados = useMemo(() => {
    const filtered = cctsData.filter((item) => {
      const matchCNPJLaboral = !filtroCNPJLaboral || (item.cnpj_entidade_laboral || '').toLowerCase().includes(filtroCNPJLaboral.toLowerCase())
      const matchCNPJPatronal = !filtroCNPJPatronal || (item.cnpj_entidade_patronal || '').toLowerCase().includes(filtroCNPJPatronal.toLowerCase())
      const norm = statusNormalizado(item.status)
      const matchStatus = !filtroStatus || norm === filtroStatus
      const matchDataUltimaBusca = matchFiltroData(item.created_at, filtroDataUltimaBusca, operadorFiltroDataUltimaBusca)
      return matchCNPJLaboral && matchCNPJPatronal && matchStatus && matchDataUltimaBusca
    })

    return filtered.sort((a, b) => {
      let aVal: unknown = sortBy === 'id_scgp' ? a.id_scgp : a[sortBy]
      let bVal: unknown = sortBy === 'id_scgp' ? b.id_scgp : b[sortBy]
      if (sortBy === 'created_at' || sortBy === 'vigencia_inicio' || sortBy === 'vigencia_fim' || sortBy === 'data_importacao') {
        aVal = aVal ? new Date(String(aVal)).getTime() : 0
        bVal = bVal ? new Date(String(bVal)).getTime() : 0
      } else if (sortBy === 'id' || sortBy === 'cct_convencao_id') {
        aVal = Number(aVal) || 0
        bVal = Number(bVal) || 0
      } else if (sortBy === 'id_scgp') {
        aVal = aVal != null ? Number(aVal) : NaN
        bVal = bVal != null ? Number(bVal) : NaN
      } else if (sortBy === 'resposta') {
        aVal = aVal ? JSON.stringify(aVal) : ''
        bVal = bVal ? JSON.stringify(bVal) : ''
      } else {
        aVal = String(aVal ?? '').toLowerCase()
        bVal = String(bVal ?? '').toLowerCase()
      }
      if ((aVal as number | string) < (bVal as number | string)) return sortOrder === 'asc' ? -1 : 1
      if ((aVal as number | string) > (bVal as number | string)) return sortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [cctsData, filtroCNPJLaboral, filtroCNPJPatronal, filtroStatus, filtroDataUltimaBusca, operadorFiltroDataUltimaBusca, sortBy, sortOrder])

  const aditivosFiltrados = useMemo(() => {
    const filtered = aditivosData.filter((item) => {
      const matchCnpj = !filtroCnpjAditivo || (item.cnpj || '').toLowerCase().includes(filtroCnpjAditivo.toLowerCase())
      const norm = statusNormalizadoAditivo(item.status)
      const matchStatus = !filtroStatusAditivo || norm === filtroStatusAditivo
      const matchData = matchFiltroData(item.created_at, filtroDataAditivo, operadorFiltroDataAditivo)
      return matchCnpj && matchStatus && matchData
    })

    return filtered.sort((a, b) => {
      let aVal: unknown = a[sortByAditivo]
      let bVal: unknown = b[sortByAditivo]
      if (sortByAditivo === 'created_at' || sortByAditivo === 'vigencia_inicio' || sortByAditivo === 'vigencia_fim') {
        aVal = aVal ? new Date(String(aVal)).getTime() : 0
        bVal = bVal ? new Date(String(bVal)).getTime() : 0
      } else {
        aVal = String(aVal ?? '').toLowerCase()
        bVal = String(bVal ?? '').toLowerCase()
      }
      if ((aVal as number | string) < (bVal as number | string)) return sortOrderAditivo === 'asc' ? -1 : 1
      if ((aVal as number | string) > (bVal as number | string)) return sortOrderAditivo === 'asc' ? 1 : -1
      return 0
    })
  }, [aditivosData, filtroCnpjAditivo, filtroStatusAditivo, filtroDataAditivo, operadorFiltroDataAditivo, sortByAditivo, sortOrderAditivo])

  useEffect(() => {
    setCurrentPage(1)
  }, [filtroCNPJLaboral, filtroCNPJPatronal, filtroStatus, filtroDataUltimaBusca, operadorFiltroDataUltimaBusca])

  useEffect(() => {
    setCurrentPageAditivo(1)
  }, [filtroCnpjAditivo, filtroStatusAditivo, filtroDataAditivo, operadorFiltroDataAditivo])

  const totalPages = Math.ceil(cctsFiltrados.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const cctsPaginados = cctsFiltrados.slice(startIndex, endIndex)

  const totalPagesAditivo = Math.ceil(aditivosFiltrados.length / itemsPerPage)
  const startIndexAditivo = (currentPageAditivo - 1) * itemsPerPage
  const endIndexAditivo = startIndexAditivo + itemsPerPage
  const aditivosPaginados = aditivosFiltrados.slice(startIndexAditivo, endIndexAditivo)

  const handleRowClick = (item: CctBuscaLista) => {
    setSelectedCCT(item)
    setIsDetailsModalOpen(true)
  }

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false)
    setSelectedCCT(null)
    setDetailsModalTab('dados')
  }

  const handleAditivoRowClick = (item: CctBuscaAditivo) => {
    setSelectedAditivo(item)
    setIsAditivoDetailsOpen(true)
  }

  const handleCloseAditivoDetails = () => {
    setIsAditivoDetailsOpen(false)
    setSelectedAditivo(null)
    setAditivoDetailsTab('dados')
  }

  const handleEnviarParaScgp = (convencaoId: number, idBusca: number) => {
    if (!canInsert) return
    setEnviarScgpModalConvencaoId(convencaoId)
    setEnviarScgpModalIdBusca(idBusca)
    setEnviarScgpModalStatus('confirm')
    setEnviarScgpModalError(null)
    setIsEnviarScgpModalOpen(true)
  }

  const handleConfirmEnviarScgp = async () => {
    if (!canInsert) return
    const id = enviarScgpModalConvencaoId
    if (id == null) return
    setEnviarScgpModalStatus('loading')
    try {
      const body: { id: number; id_busca?: number } = { id }
      if (enviarScgpModalIdBusca != null) body.id_busca = enviarScgpModalIdBusca
      const res = await fetch(WEBHOOK_INSERIR_CCT_SCGP, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
      if (!res.ok) throw new Error(`Erro ${res.status}`)
      setEnviarScgpModalStatus('success')
      fetchLista()
    } catch (err: unknown) {
      setEnviarScgpModalError(err instanceof Error ? err.message : 'Não foi possível enviar para o SCGP.')
      setEnviarScgpModalStatus('error')
    }
  }

  const handleCloseEnviarScgpModal = () => {
    setIsEnviarScgpModalOpen(false)
    setEnviarScgpModalStatus('idle')
    setEnviarScgpModalError(null)
    setEnviarScgpModalConvencaoId(null)
    setEnviarScgpModalIdBusca(null)
  }

  const handleCancelEnviarScgpModal = () => {
    setIsEnviarScgpModalOpen(false)
    setEnviarScgpModalStatus('idle')
    setEnviarScgpModalError(null)
    setEnviarScgpModalConvencaoId(null)
    setEnviarScgpModalIdBusca(null)
  }

  const toggleSort = (key: string) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(key as SortKeyCct)
      setSortOrder('asc')
    }
  }

  const toggleSortAditivo = (key: string) => {
    if (sortByAditivo === key) {
      setSortOrderAditivo(sortOrderAditivo === 'asc' ? 'desc' : 'asc')
    } else {
      setSortByAditivo(key as SortKeyAditivo)
      setSortOrderAditivo('asc')
    }
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" className="mx-auto mb-4" />
          <Text size="lg" className="text-gray-600 dark:text-gray-400">
            Carregando CCTs...
          </Text>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-8 w-8 text-red-500 mx-auto mb-4" />
          <Text size="lg" className="text-red-600 dark:text-red-400">
            Erro ao carregar CCTs: {error}
          </Text>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
        <PermissionGuard 
          moduleName="ccts_lista" 
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                  Acesso Negado
                </Text>
                <Text size="sm" color="muted">
                  Você não tem permissão para acessar esta página.
                </Text>
              </div>
            </div>
          }
        >
            <div className="h-full flex flex-col">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                      {moduleTitle}
                    </Heading>
                    <Text size="sm" color="muted" className="mt-1">
                      {moduleDescription}
                    </Text>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <Tooltip content="Recarregar buscas de CCTs e aditivos do banco sem sair da página">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => fetchLista({ silent: true })}
                        disabled={isRefreshing}
                        className="inline-flex items-center gap-2"
                        aria-label="Atualizar dados da lista de CCTs"
                      >
                        <ArrowPathIcon
                          className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`}
                          aria-hidden
                        />
                        Atualizar dados
                      </Button>
                    </Tooltip>
                    {refreshError && (
                      <Text size="xs" className="text-red-600 dark:text-red-400 max-w-[16rem] text-right">
                        {refreshError}
                      </Text>
                    )}
                  </div>
                </div>
              </div>

              {activeTab === 'ccts' && (
                <>
              <div className="px-6 py-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-gradient-to-br from-primary-500 to-primary-600 shadow-lg rounded-lg p-6 text-white">
                    <div className="text-center">
                      <Text size="3xl" className="text-white font-bold mb-2">
                        {cctsFiltrados.length}
                      </Text>
                      <Text size="sm" className="text-white">
                        Total de Buscas
                      </Text>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 shadow-lg rounded-lg p-6 text-white">
                    <div className="text-center">
                      <Text size="3xl" className="text-white font-bold mb-2">
                        {cctsFiltrados.filter(c => statusNormalizado(c.status) === 'NÃO LOCALIZADA').length}
                      </Text>
                      <Text size="sm" className="text-white">
                        NÃO LOCALIZADA
                      </Text>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-green-500 to-green-600 shadow-lg rounded-lg p-6 text-white">
                    <div className="text-center">
                      <Text size="3xl" className="text-white font-bold mb-2">
                        {cctsFiltrados.filter(c => statusNormalizado(c.status) === 'IMPORTADO SCGP').length}
                      </Text>
                      <Text size="sm" className="text-white">
                        IMPORTADO SCGP
                      </Text>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg rounded-lg p-6 text-white">
                    <div className="text-center">
                      <Text size="3xl" className="text-white font-bold mb-2">
                        {cctsFiltrados.filter(c => statusNormalizado(c.status) === 'DISPONÍVEL PARA IMPORTAR').length}
                      </Text>
                      <Text size="sm" className="text-white">
                        DISPONÍVEL PARA IMPORTAR
                      </Text>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">
                      CNPJ Laboral
                    </Text>
                    <Input
                      type="text"
                      placeholder="Digite o CNPJ laboral..."
                      value={filtroCNPJLaboral}
                      onChange={(e) => setFiltroCNPJLaboral(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">
                      CNPJ Patronal
                    </Text>
                    <Input
                      type="text"
                      placeholder="Digite o CNPJ patronal..."
                      value={filtroCNPJPatronal}
                      onChange={(e) => setFiltroCNPJPatronal(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">
                      Status
                    </Text>
                    <Select
                      value={filtroStatus}
                      onChange={(e) => setFiltroStatus(e.target.value)}
                      className="w-full"
                    >
                      <option value="">Todos os status</option>
                      <option value="NÃO LOCALIZADA">NÃO LOCALIZADA</option>
                      <option value="IMPORTADO SCGP">IMPORTADO SCGP</option>
                      <option value="DISPONÍVEL PARA IMPORTAR">DISPONÍVEL PARA IMPORTAR</option>
                    </Select>
                  </div>
                </div>
                <div className="flex items-end gap-2 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <DateFilterControls
                      operador={operadorFiltroDataUltimaBusca}
                      setOperador={setOperadorFiltroDataUltimaBusca}
                      tipo={tipoFiltroDataUltimaBusca}
                      setTipo={setTipoFiltroDataUltimaBusca}
                      valor={filtroDataUltimaBusca}
                      setValor={setFiltroDataUltimaBusca}
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setFiltroCNPJLaboral('')
                      setFiltroCNPJPatronal('')
                      setFiltroStatus('')
                      setFiltroDataUltimaBusca('')
                      setTipoFiltroDataUltimaBusca('data')
                      setOperadorFiltroDataUltimaBusca('igual')
                    }}
                    className="shrink-0 mb-0.5"
                  >
                    Limpar Filtros
                  </Button>
                </div>
              </div>
                </>
              )}

              {activeTab === 'aditivos' && (
                <>
              <div className="px-6 py-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-gradient-to-br from-primary-500 to-primary-600 shadow-lg rounded-lg p-6 text-white">
                    <div className="text-center">
                      <Text size="3xl" className="text-white font-bold mb-2">
                        {aditivosFiltrados.length}
                      </Text>
                      <Text size="sm" className="text-white">
                        Total de Buscas
                      </Text>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 shadow-lg rounded-lg p-6 text-white">
                    <div className="text-center">
                      <Text size="3xl" className="text-white font-bold mb-2">
                        {aditivosFiltrados.filter((c) => statusNormalizadoAditivo(c.status) === 'NÃO LOCALIZADA').length}
                      </Text>
                      <Text size="sm" className="text-white">
                        NÃO LOCALIZADA
                      </Text>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-green-500 to-green-600 shadow-lg rounded-lg p-6 text-white">
                    <div className="text-center">
                      <Text size="3xl" className="text-white font-bold mb-2">
                        {aditivosFiltrados.filter((c) => statusNormalizadoAditivo(c.status) === 'IMPORTADO SCGP').length}
                      </Text>
                      <Text size="sm" className="text-white">
                        IMPORTADO SCGP
                      </Text>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg rounded-lg p-6 text-white">
                    <div className="text-center">
                      <Text size="3xl" className="text-white font-bold mb-2">
                        {aditivosFiltrados.filter((c) => {
                          const n = statusNormalizadoAditivo(c.status)
                          return n === 'DISPONÍVEL PARA IMPORTAR' || n === 'LOCALIZADA'
                        }).length}
                      </Text>
                      <Text size="sm" className="text-white">
                        DISPONÍVEL PARA IMPORTAR
                      </Text>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">
                      CNPJ
                    </Text>
                    <Input
                      type="text"
                      placeholder="Digite o CNPJ..."
                      value={filtroCnpjAditivo}
                      onChange={(e) => setFiltroCnpjAditivo(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">
                      Status
                    </Text>
                    <Select
                      value={filtroStatusAditivo}
                      onChange={(e) => setFiltroStatusAditivo(e.target.value)}
                      className="w-full"
                    >
                      <option value="">Todos os status</option>
                      <option value="NÃO LOCALIZADA">NÃO LOCALIZADA</option>
                      <option value="LOCALIZADA">LOCALIZADA</option>
                      <option value="DISPONÍVEL PARA IMPORTAR">DISPONÍVEL PARA IMPORTAR</option>
                      <option value="IMPORTADO SCGP">IMPORTADO SCGP</option>
                      <option value="SEM ALTERAÇÃO">SEM ALTERAÇÃO</option>
                    </Select>
                  </div>
                </div>
                <div className="flex items-end gap-2 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <DateFilterControls
                      operador={operadorFiltroDataAditivo}
                      setOperador={setOperadorFiltroDataAditivo}
                      tipo={tipoFiltroDataAditivo}
                      setTipo={setTipoFiltroDataAditivo}
                      valor={filtroDataAditivo}
                      setValor={setFiltroDataAditivo}
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setFiltroCnpjAditivo('')
                      setFiltroStatusAditivo('')
                      setFiltroDataAditivo('')
                      setTipoFiltroDataAditivo('data')
                      setOperadorFiltroDataAditivo('igual')
                    }}
                    className="shrink-0 mb-0.5"
                  >
                    Limpar Filtros
                  </Button>
                </div>
              </div>
                </>
              )}

              <div className="px-6 pt-4 border-b border-gray-200 dark:border-gray-700">
                <div className="border-b border-gray-200 dark:border-gray-700">
                  <nav className="-mb-px flex flex-wrap gap-x-8">
                    <button
                      type="button"
                      onClick={() => setActiveTab('ccts')}
                      className={tabClass(activeTab === 'ccts')}
                    >
                      CCTs
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('aditivos')}
                      className={tabClass(activeTab === 'aditivos')}
                    >
                      Aditivos de CCTs
                    </button>
                  </nav>
                </div>
              </div>

              {activeTab === 'ccts' && (
              <div className="flex-1 px-6 py-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                      CCTs
                    </Heading>
                  </div>
                  
                  <div className="flex-1 overflow-auto">
                    <table className="w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm text-center">
                      <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10 text-xs text-gray-500 dark:text-gray-400 uppercase">
                        <tr>
                          <SortableTableHeader sortKey="created_at" currentSort={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="px-2 py-3 text-center">
                            Data
                          </SortableTableHeader>
                          <th className="px-2 py-3 text-center">
                            <div className="flex items-center justify-center gap-1.5">
                              <span>STATUS</span>
                              <Tooltip content={
                                <div className="space-y-2 text-left" style={{ minWidth: 0, overflowWrap: 'break-word', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                                  <div>
                                    <span className="font-semibold text-yellow-400">NÃO LOCALIZADA:</span>
                                    <span className="block mt-0.5">Busca realizada no Sistema do MTE e não foi encontrada a CCT.</span>
                                  </div>
                                  <div>
                                    <span className="font-semibold text-green-400">IMPORTADO SCGP:</span>
                                    <span className="block mt-0.5">CCT localizada no sistema do MTE e importada para o SCGP.</span>
                                  </div>
                                  <div>
                                    <span className="font-semibold text-blue-400">DISPONÍVEL PARA IMPORTAR:</span>
                                    <span className="block mt-0.5">CCT localizada no sistema do MTE e disponível para ser importada para o SCGP. Basta usar o botão Enviar para o SCGP.</span>
                                  </div>
                                </div>
                              } constrainWidth>
                                <QuestionMarkCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 cursor-help flex-shrink-0 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" />
                              </Tooltip>
                            </div>
                          </th>
                          <SortableTableHeader sortKey="id_scgp" currentSort={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="px-2 py-3 text-center">
                            ID SCGP
                          </SortableTableHeader>
                          <SortableTableHeader sortKey="vigencia_inicio" currentSort={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="px-2 py-3 text-center">
                            Vigência início
                          </SortableTableHeader>
                          <SortableTableHeader sortKey="vigencia_fim" currentSort={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="px-2 py-3 text-center">
                            Vigência fim
                          </SortableTableHeader>
                          <SortableTableHeader sortKey="codigo" currentSort={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="px-2 py-3 text-center">
                            N° MTE
                          </SortableTableHeader>
                          <th className="px-2 py-3 text-center">Documento</th>
                          <th className="px-2 py-3 text-center">Download</th>
                          {canInsert && <th className="px-2 py-3 text-center w-20">Ações</th>}
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {cctsPaginados.map((item) => {
                          const statusNorm = statusNormalizado(item.status)
                          const statusClass = statusColorClass(statusNorm)
                          const statusLabel = statusNorm === 'NÃO LOCALIZADA' ? 'NÃO LOCALIZADA' : (item.status ?? '—')
                          const isDisponivel = statusNorm === 'DISPONÍVEL PARA IMPORTAR'
                          return (
                            <tr
                              key={item.id}
                              className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                              onClick={() => handleRowClick(item)}
                            >
                              <td className="px-2 py-2 text-center text-gray-700 dark:text-gray-300 whitespace-nowrap">{formatDate(item.created_at)}</td>
                              <td className="px-2 py-2 text-center">
                                <span className={`text-sm font-medium ${statusClass}`}>{statusLabel}</span>
                              </td>
                              <td className="px-2 py-2 text-center text-gray-700 dark:text-gray-300 whitespace-nowrap">{item.id_scgp ?? '—'}</td>
                              <td className="px-2 py-2 text-center text-gray-700 dark:text-gray-300 whitespace-nowrap">{formatDateShort(item.vigencia_inicio)}</td>
                              <td className="px-2 py-2 text-center text-gray-700 dark:text-gray-300 whitespace-nowrap">{formatDateShort(item.vigencia_fim)}</td>
                              <td className="px-2 py-2 text-center text-gray-700 dark:text-gray-300">{item.codigo ?? '—'}</td>
                              <td className="px-2 py-2 text-center">
                                {item.link_documento ? (
                                  <a href={item.link_documento} target="_blank" rel="noopener noreferrer" className="text-primary-600 dark:text-primary-400 hover:underline text-xs" onClick={e => e.stopPropagation()}>Abrir</a>
                                ) : '—'}
                              </td>
                              <td className="px-2 py-2 text-center">
                                {item.link_download ? (
                                  <a href={item.link_download} target="_blank" rel="noopener noreferrer" className="text-primary-600 dark:text-primary-400 hover:underline text-xs" onClick={e => e.stopPropagation()}>Baixar</a>
                                ) : '—'}
                              </td>
                              {canInsert && (
                                <td className="px-2 py-2 text-center" onClick={e => e.stopPropagation()}>
                                  {isDisponivel && item.cct_convencao_id != null ? (
                                    <Tooltip content="Enviar para SCGP">
                                      <button
                                        type="button"
                                        disabled={isEnviarScgpModalOpen}
                                        onClick={() => handleEnviarParaScgp(item.cct_convencao_id!, item.id)}
                                        className="inline-flex items-center justify-center rounded-md p-2 text-gray-600 hover:text-green-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-green-400 dark:hover:bg-gray-700 focus:outline-none transition-colors disabled:opacity-50 disabled:pointer-events-none"
                                        aria-label="Enviar para SCGP"
                                      >
                                        <PaperAirplaneIcon className="h-5 w-5" />
                                      </button>
                                    </Tooltip>
                                  ) : (
                                    <span className="text-gray-400 dark:text-gray-500">—</span>
                                  )}
                                </td>
                              )}
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                  
                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    startIndex={startIndex}
                    endIndex={endIndex}
                    total={cctsFiltrados.length}
                    onPageChange={setCurrentPage}
                    onPrevious={() => { if (currentPage > 1) setCurrentPage(currentPage - 1) }}
                    onNext={() => { if (currentPage < totalPages) setCurrentPage(currentPage + 1) }}
                  />
                </div>
              </div>
              )}

              {activeTab === 'aditivos' && (
              <div className="flex-1 px-6 py-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                      Aditivos de CCTs
                    </Heading>
                  </div>

                  <div className="flex-1 overflow-auto">
                    <table className="w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm text-center">
                      <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10 text-xs text-gray-500 dark:text-gray-400 uppercase">
                        <tr>
                          <SortableTableHeader sortKey="created_at" currentSort={sortByAditivo} sortOrder={sortOrderAditivo} onSort={toggleSortAditivo} className="px-2 py-3 text-center">
                            Data
                          </SortableTableHeader>
                          <th className="px-2 py-3 text-center">
                            <div className="flex items-center justify-center gap-1.5">
                              <span>STATUS</span>
                              <Tooltip content={
                                <div className="space-y-2 text-left" style={{ minWidth: 0, overflowWrap: 'break-word', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                                  <div>
                                    <span className="font-semibold text-yellow-400">NÃO LOCALIZADA:</span>
                                    <span className="block mt-0.5">Busca realizada no Sistema do MTE e não foi encontrado o aditivo.</span>
                                  </div>
                                  <div>
                                    <span className="font-semibold text-green-400">IMPORTADO SCGP:</span>
                                    <span className="block mt-0.5">Aditivo localizado no sistema do MTE e importado para o SCGP.</span>
                                  </div>
                                  <div>
                                    <span className="font-semibold text-blue-400">DISPONÍVEL PARA IMPORTAR:</span>
                                    <span className="block mt-0.5">Aditivo localizado no sistema do MTE e disponível para ser importado para o SCGP.</span>
                                  </div>
                                  <div>
                                    <span className="font-semibold text-blue-400">LOCALIZADA:</span>
                                    <span className="block mt-0.5">Aditivo localizado no sistema do MTE.</span>
                                  </div>
                                  <div>
                                    <span className="font-semibold text-gray-300">SEM ALTERAÇÃO:</span>
                                    <span className="block mt-0.5">Aditivo marcado manualmente como sem alteração.</span>
                                  </div>
                                </div>
                              } constrainWidth>
                                <QuestionMarkCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 cursor-help flex-shrink-0 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" />
                              </Tooltip>
                            </div>
                          </th>
                          <SortableTableHeader sortKey="cnpj" currentSort={sortByAditivo} sortOrder={sortOrderAditivo} onSort={toggleSortAditivo} className="px-2 py-3 text-center">
                            CNPJ
                          </SortableTableHeader>
                          <SortableTableHeader sortKey="indice" currentSort={sortByAditivo} sortOrder={sortOrderAditivo} onSort={toggleSortAditivo} className="px-2 py-3 text-center">
                            Índice
                          </SortableTableHeader>
                          <SortableTableHeader sortKey="registro" currentSort={sortByAditivo} sortOrder={sortOrderAditivo} onSort={toggleSortAditivo} className="px-2 py-3 text-center">
                            Registro
                          </SortableTableHeader>
                          <SortableTableHeader sortKey="vigencia_inicio" currentSort={sortByAditivo} sortOrder={sortOrderAditivo} onSort={toggleSortAditivo} className="px-2 py-3 text-center">
                            Vigência início
                          </SortableTableHeader>
                          <SortableTableHeader sortKey="vigencia_fim" currentSort={sortByAditivo} sortOrder={sortOrderAditivo} onSort={toggleSortAditivo} className="px-2 py-3 text-center">
                            Vigência fim
                          </SortableTableHeader>
                          <th className="px-2 py-3 text-center">Documento</th>
                          <th className="px-2 py-3 text-center">Download</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {aditivosPaginados.map((item) => {
                          const norm = statusNormalizadoAditivo(item.status)
                          const statusClass = statusColorClass(norm)
                          const statusLabel = norm || (item.status ?? '—')
                          return (
                            <tr
                              key={item.id}
                              className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                              onClick={() => handleAditivoRowClick(item)}
                            >
                              <td className="px-2 py-2 text-center text-gray-700 dark:text-gray-300 whitespace-nowrap">{formatDate(item.created_at)}</td>
                              <td className="px-2 py-2 text-center">
                                <span className={`text-sm font-medium ${statusClass}`}>{statusLabel}</span>
                              </td>
                              <td className="px-2 py-2 text-center text-gray-700 dark:text-gray-300 whitespace-nowrap">{item.cnpj || '—'}</td>
                              <td className="px-2 py-2 text-center text-gray-700 dark:text-gray-300">{item.indice ?? '—'}</td>
                              <td className="px-2 py-2 text-center text-gray-700 dark:text-gray-300">{item.registro ?? '—'}</td>
                              <td className="px-2 py-2 text-center text-gray-700 dark:text-gray-300 whitespace-nowrap">{formatDateShort(item.vigencia_inicio)}</td>
                              <td className="px-2 py-2 text-center text-gray-700 dark:text-gray-300 whitespace-nowrap">{formatDateShort(item.vigencia_fim)}</td>
                              <td className="px-2 py-2 text-center">
                                {item.link_documento ? (
                                  <a href={item.link_documento} target="_blank" rel="noopener noreferrer" className="text-primary-600 dark:text-primary-400 hover:underline text-xs" onClick={(e) => e.stopPropagation()}>Abrir</a>
                                ) : '—'}
                              </td>
                              <td className="px-2 py-2 text-center">
                                {item.link_download ? (
                                  <a href={item.link_download} target="_blank" rel="noopener noreferrer" className="text-primary-600 dark:text-primary-400 hover:underline text-xs" onClick={(e) => e.stopPropagation()}>Baixar</a>
                                ) : '—'}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>

                  <PaginationControls
                    currentPage={currentPageAditivo}
                    totalPages={totalPagesAditivo}
                    startIndex={startIndexAditivo}
                    endIndex={endIndexAditivo}
                    total={aditivosFiltrados.length}
                    onPageChange={setCurrentPageAditivo}
                    onPrevious={() => { if (currentPageAditivo > 1) setCurrentPageAditivo(currentPageAditivo - 1) }}
                    onNext={() => { if (currentPageAditivo < totalPagesAditivo) setCurrentPageAditivo(currentPageAditivo + 1) }}
                  />
                </div>
              </div>
              )}
            </div>
          </PermissionGuard>
      </div>

      {isEnviarScgpModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" />
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
              <div className="flex flex-col items-center gap-4 py-4">
                {enviarScgpModalStatus === 'confirm' && (
                  <>
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                      <QuestionMarkCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                    </div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 text-center">
                      Deseja confirmar o envio desta convenção para o SCGP?
                    </Text>
                  </>
                )}
                {enviarScgpModalStatus === 'loading' && (
                  <>
                    <LoadingSpinner size="sm" className="h-12 w-12 text-primary-600 dark:text-primary-400" />
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 text-center">
                      Enviando para o SCGP...
                    </Text>
                  </>
                )}
                {enviarScgpModalStatus === 'success' && (
                  <>
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                      <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 text-center">
                      Enviado com sucesso. Feche para atualizar os dados.
                    </Text>
                  </>
                )}
                {enviarScgpModalStatus === 'error' && (
                  <>
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                      <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                    </div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 text-center">
                      {enviarScgpModalError ?? 'Erro ao enviar para o SCGP.'}
                    </Text>
                  </>
                )}
              </div>
              <div className="mt-4 flex justify-center gap-2">
                {enviarScgpModalStatus === 'confirm' && (
                  <>
                    <Button variant="outline" size="sm" onClick={handleCancelEnviarScgpModal}>
                      Cancelar
                    </Button>
                    <Button variant="solid" size="sm" onClick={handleConfirmEnviarScgp}>
                      Confirmar
                    </Button>
                  </>
                )}
                {(enviarScgpModalStatus === 'loading' || enviarScgpModalStatus === 'success' || enviarScgpModalStatus === 'error') && (
                  <Button variant="solid" size="sm" onClick={handleCloseEnviarScgpModal}>
                    Fechar
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {isDetailsModalOpen && selectedCCT && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={handleCloseDetailsModal} />
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6 max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                  Registro do Histórico de buscas de CCTs no MTE
                </Heading>
                <button
                  type="button"
                  onClick={handleCloseDetailsModal}
                  className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none dark:hover:text-gray-300"
                  aria-label="Fechar"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="flex border-b border-gray-200 dark:border-gray-600 mb-4">
                <button
                  type="button"
                  onClick={() => setDetailsModalTab('dados')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${detailsModalTab === 'dados' ? 'border-primary-600 text-primary-600 dark:text-primary-400 dark:border-primary-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'}`}
                >
                  Dados
                </button>
                <button
                  type="button"
                  onClick={() => setDetailsModalTab('resposta')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${detailsModalTab === 'resposta' ? 'border-primary-600 text-primary-600 dark:text-primary-400 dark:border-primary-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'}`}
                >
                  Resposta
                </button>
                <button
                  type="button"
                  onClick={() => setDetailsModalTab('clausulas')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${detailsModalTab === 'clausulas' ? 'border-primary-600 text-primary-600 dark:text-primary-400 dark:border-primary-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'}`}
                >
                  Cláusulas específicas
                </button>
              </div>
              <div className="overflow-y-auto flex-1 min-h-0">
                {detailsModalTab === 'dados' ? (
                  <div className="space-y-3 text-sm">
                    {[
                      { label: 'ID', value: selectedCCT.id },
                      { label: 'Data', value: formatDate(selectedCCT.created_at) },
                      { label: 'CCT Convenção ID', value: selectedCCT.cct_convencao_id != null ? String(selectedCCT.cct_convencao_id) : '—' },
                      { label: 'Status', value: statusNormalizado(selectedCCT.status) || '—' },
                      { label: 'Link documento', value: selectedCCT.link_documento ? (
                        <a href={selectedCCT.link_documento} target="_blank" rel="noopener noreferrer" className="text-primary-600 dark:text-primary-400 hover:underline" onClick={e => e.stopPropagation()}>Abrir</a>
                      ) : '—' },
                      { label: 'Link download', value: selectedCCT.link_download ? (
                        <a href={selectedCCT.link_download} target="_blank" rel="noopener noreferrer" className="text-primary-600 dark:text-primary-400 hover:underline" onClick={e => e.stopPropagation()}>Baixar</a>
                      ) : '—' },
                      { label: 'Vigência início', value: formatDateShort(selectedCCT.vigencia_inicio) },
                      { label: 'Vigência fim', value: formatDateShort(selectedCCT.vigencia_fim) },
                      { label: 'Partes', value: selectedCCT.partes ?? '—' },
                      { label: 'N° MTE', value: selectedCCT.codigo ?? '—' },
                      { label: 'Data importação', value: selectedCCT.data_importacao ? formatDateShort(selectedCCT.data_importacao) : '—' },
                      { label: 'ID SCGP', value: selectedCCT.id_scgp != null ? String(selectedCCT.id_scgp) : '—' },
                    ].map(({ label, value }) => (
                      <div key={label} className="flex flex-col gap-0.5">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</span>
                        <span className="text-gray-700 dark:text-gray-300 break-words">{value as React.ReactNode}</span>
                      </div>
                    ))}
                  </div>
                ) : detailsModalTab === 'resposta' ? (
                  <div className="text-sm">
                    {isEmptyText(selectedCCT.resposta) ? (
                      <span className="text-gray-500 dark:text-gray-400">Nenhum conteúdo.</span>
                    ) : (
                      <pre className="whitespace-pre-wrap break-words font-sans text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/50 p-3 rounded-lg overflow-x-auto">{String(selectedCCT.resposta)}</pre>
                    )}
                  </div>
                ) : (
                  <div className="text-sm">
                    {isEmptyText(selectedCCT.clausulas_especificas) ? (
                      <span className="text-gray-500 dark:text-gray-400">Nenhum conteúdo.</span>
                    ) : (
                      <pre className="whitespace-pre-wrap break-words font-sans text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/50 p-3 rounded-lg overflow-x-auto">{selectedCCT.clausulas_especificas}</pre>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {isAditivoDetailsOpen && selectedAditivo && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={handleCloseAditivoDetails} />
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6 max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                  Registro do Histórico de buscas de Aditivos de CCTs no MTE
                </Heading>
                <button
                  type="button"
                  onClick={handleCloseAditivoDetails}
                  className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none dark:hover:text-gray-300"
                  aria-label="Fechar"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="flex border-b border-gray-200 dark:border-gray-600 mb-4">
                <button
                  type="button"
                  onClick={() => setAditivoDetailsTab('dados')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${aditivoDetailsTab === 'dados' ? 'border-primary-600 text-primary-600 dark:text-primary-400 dark:border-primary-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'}`}
                >
                  Dados
                </button>
                <button
                  type="button"
                  onClick={() => setAditivoDetailsTab('resposta')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${aditivoDetailsTab === 'resposta' ? 'border-primary-600 text-primary-600 dark:text-primary-400 dark:border-primary-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'}`}
                >
                  Resposta
                </button>
              </div>
              <div className="overflow-y-auto flex-1 min-h-0">
                {aditivoDetailsTab === 'dados' ? (
                  <div className="space-y-3 text-sm">
                    {[
                      { label: 'ID', value: selectedAditivo.id },
                      { label: 'Data', value: formatDate(selectedAditivo.created_at) },
                      { label: 'CCT Convenção ID', value: selectedAditivo.cct_convencao_id != null ? String(selectedAditivo.cct_convencao_id) : '—' },
                      { label: 'CNPJ', value: selectedAditivo.cnpj || '—' },
                      { label: 'Índice', value: selectedAditivo.indice ?? '—' },
                      { label: 'Registro', value: selectedAditivo.registro ?? '—' },
                      { label: 'Solicitação', value: selectedAditivo.solicitacao ?? '—' },
                      { label: 'Status', value: statusNormalizadoAditivo(selectedAditivo.status) || '—' },
                      { label: 'Vigência início', value: formatDateShort(selectedAditivo.vigencia_inicio) },
                      { label: 'Vigência fim', value: formatDateShort(selectedAditivo.vigencia_fim) },
                      { label: 'Partes', value: selectedAditivo.partes ?? '—' },
                      { label: 'Link documento', value: selectedAditivo.link_documento ? (
                        <a href={selectedAditivo.link_documento} target="_blank" rel="noopener noreferrer" className="text-primary-600 dark:text-primary-400 hover:underline" onClick={(e) => e.stopPropagation()}>Abrir</a>
                      ) : '—' },
                      { label: 'Link download', value: selectedAditivo.link_download ? (
                        <a href={selectedAditivo.link_download} target="_blank" rel="noopener noreferrer" className="text-primary-600 dark:text-primary-400 hover:underline" onClick={(e) => e.stopPropagation()}>Baixar</a>
                      ) : '—' },
                      { label: 'Mensagem de erro', value: selectedAditivo.error_message ?? '—' },
                    ].map(({ label, value }) => (
                      <div key={label} className="flex flex-col gap-0.5">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</span>
                        <span className="text-gray-700 dark:text-gray-300 break-words">{value as React.ReactNode}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm">
                    {isEmptyText(selectedAditivo.resposta) ? (
                      <span className="text-gray-500 dark:text-gray-400">Nenhum conteúdo.</span>
                    ) : (
                      <pre className="whitespace-pre-wrap break-words font-sans text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/50 p-3 rounded-lg overflow-x-auto">{selectedAditivo.resposta}</pre>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default CCTLista
